import { apiClient } from '@app/lib'
import { type SugarPaymentSetupType, type SugarPaymentSetupViewResponse } from '@app/types'
import { useQuery } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

// Returns the WHOLE envelope rather than unwrapping to data.data like useMe, because the screen must
// distinguish three outcomes: transport error, success:false, and data === null ("nothing on file").
//
// initialData IS accepted here, which looks like it contradicts the note that used to live on this
// hook: initialData: null counts as data in TanStack v5 (hasData = data !== undefined), which would
// make "nothing on file" indistinguishable from "not yet fetched" — but that ambiguity only exists
// when null is handed over WITHOUT having fetched. payment-info.tsx's getServerSideProps has already
// called the Sugar endpoint by the time initialData reaches here, so a null value is a real, fetched
// answer, not a placeholder. Callers that cannot make that guarantee should keep omitting the argument.
export const usePaymentSetupDetails = <T>(
  paymentSetupType: SugarPaymentSetupType,
  initialData?: SugarPaymentSetupViewResponse<T>
) => {
  const session = useSession()

  return useQuery({
    enabled: session.status === 'authenticated',
    initialData,
    queryKey: ['paymentSetup', paymentSetupType],
    queryFn: async () => {
      const { data } = await apiClient.get<SugarPaymentSetupViewResponse<T>>(`/api/payment-setup/${paymentSetupType}`)

      return data
    }
  })
}
